import RestService from './_private/RestService';
import Balance from '@models/user/Balance';
import { RequestError } from '@errors/network/RequestError';
import SmsLimitReachedError from '@errors/user/SmsLimitReachedError';
import WithdrawalRequest from '@models/user/withdrawal/WithdrawalRequest';
import { WithdrawalType } from '@models/user/enums';
import DateUtils from '@utils/date/DateUtils';
import History from '@models/user/history/History';
import HistoryMapper from '@core/mappers/user/HistoryMapper';
import IHistoryDto from '@core/mappers/user/dtos/IHistoryDto';
import ValidationError from '@errors/network/ValidationError';
import { getDetails } from '@core/services/authentication/AuthenticationService';
import WithdrawalResponse from '@models/user/withdrawal/WithdrawalResponse';
import WithdrawalAbility from '@core/models/user/withdrawal/WithdrawalAbility';
import LottoFavouriteDtoToModelMapper, { Favourites } from '@core/mappers/lotto/LottoFavouriteDtoToModelMapper';
import LottoFavouriteModelToDtoMapper from '@core/mappers/lotto/LottoFavouriteModelToDtoMapper';

import AuthenticationError from '@errors/authentication/AuthenticationError';
import MaxFavoritesReachedError from '@errors/lotto/MaxFavoritesReachedError';
import IResponse from '@core/utils/types/IResponse';
import IMessageDataDto from '@core/mappers/user/dtos/IMessageDataDto';
import MessageMapper from '@core/mappers/user/MessageMapper';
import ServerConfig from '@core/models/country/ServerConfig';
import * as NetworkUtils from '@utils/network/NetworkUtils';
import Message from '@core/models/user/messages/Message';
import { instanceGuard } from '@core/utils/services';
import SingletonError from '../common/errors/SingletonError';
import Singleton from '../common/Singleton';
import { smsLimitNotice } from '@core/constants';

const lottoFavouriteMapperIn = new LottoFavouriteDtoToModelMapper();
const lottoFavouriteMapperOut = new LottoFavouriteModelToDtoMapper();
const messageMapper = new MessageMapper();

export {
    WithdrawalRequest,
};

class Mappers {
    historyMapper: HistoryMapper = new HistoryMapper();
}

class UserService {

    private static instance: UserService;

    public static getInstance(): UserService {
        return instanceGuard(UserService.instance);
    }

    public static createInstance(config: ServerConfig) {
        if (!UserService.instance) {
            UserService.instance = new UserService(config);
        }
        return UserService.instance;
    }

    private restService: RestService;
    private mappers: Mappers = new Mappers();

    private constructor(private config: ServerConfig) {
        if (UserService.instance) {
            throw new SingletonError(this.constructor.name);
        }
        this.restService = new RestService(this.config);
    }

    /**
     * @throws {AuthenticationError}
     * @throws {RequestError}
     * @throws {Error}
     */
    public async getUserDetails(): Promise<any> {
        const response = await NetworkUtils.processResponse(this.restService.getUserDetails());
        return getDetails(response.data);
    }

    /**
     * @throws {AuthenticationError}
     * @throws {RequestError}
     * @throws {Error}
     */
    public async getPlayerBalance(): Promise<Balance | null> {
        const response = await NetworkUtils.processResponse(this.restService.getPlayerBalance());

        if (!response.data.Accounts.length) {
            return null;
        }

        const balance = {
            amount: 0,
            bonus: []
        } as Balance;

        response.data.Accounts.forEach((account: any) => {
            if (account.AccountType === 1) {
                balance.amount = account.Balance;
            } else {
                balance.bonus.push({
                    amount: account.Balance,
                    name: account.Name
                });
            }
        });

        return balance;
    }

    /**
     * @throws {AuthenticationError}
     * @throws {RequestError}
     * @throws {Error}
     */
    public async createDeposit(amount: number, paySafeCard = false): Promise<string> {
        const response: any =  await NetworkUtils.processResponse(
            await this.restService.createDeposit(amount, paySafeCard));

        if (response.data.PaymentStatus === 'Declined') {
            throw new ValidationError({
                nonField: [response.data.DeclineReason]
            });
        }

        return response.data.RedirectURL;
    }

    /**
     * @throws {AuthenticationError}
     * @throws {RequestError}
     * @throws {Error}
     */
    public async checkWithdrawalAbility(): Promise<WithdrawalAbility> {
        const response = await NetworkUtils.processResponse(await this.restService.checkWithdrawalAbility());

        return {
            isOnlineEligible: response.data.EligibleForOnlineWithdrawal,
            isBankTransferEligible: response.data.EligibleForBankTransferWithdrawal,
            isShopEligible: response.data.EligibleForCicoWithdrawal,
            willAnyBonusExpire: true,
            bonusesToExpire: response.data.BonusesToExpire.map((e: any) => e.BonusIncetiveFriendlyName),
        } as WithdrawalAbility;
    }

    /**
     * @throws {AuthenticationError}
     * @throws {RequestError}
     * @throws {Error}
     */
    public async createWithdrawal(withdrawalRequest: WithdrawalRequest): Promise<WithdrawalResponse> {
        const result = {} as WithdrawalResponse;
        let response;
        if ([WithdrawalType.online, WithdrawalType.bankTransfer, WithdrawalType.paySafeCard]
            .includes(withdrawalRequest.type)
        ) {
            response = await NetworkUtils.processResponse(
                this.restService.createOnlineWithdrawal(withdrawalRequest)
            );
            result.redirectUrl = response.data.RedirectURL;
        } else {
            response = await NetworkUtils.processResponse(
                this.restService.createShopWithdrawal(withdrawalRequest)
            );
            result.transactionCode = response.data.transactionCode;
            result.withdrawalDate = DateUtils.toDate(response.data.withdrawalDate);
        }

        result.status = response.data.StatusCode;

        return result;
    }

    /**
     * @throws {AuthenticationError}
     * @throws {RequestError}
     * @throws {Error}
     */
    public async fetchTransactions(page: number): Promise<History[]> {
        const response = await NetworkUtils.processResponse(this.restService.fetchTransactions(page));
        return this.mappers.historyMapper.map(response.data as IHistoryDto[], true);
    }

    /**
     * @throws {AuthenticationError}
     * @throws {RequestError}
     * @throws {Error}
     */
    public async fetchCasinoPlays(page: number): Promise<History[]> {
        const response = await NetworkUtils.processResponse(this.restService.fetchCasinoPlays(page));
        return this.mappers.historyMapper.map(response.data as IHistoryDto[], true);
    }

    /**
     * @throws {AuthenticationError}
     * @throws {RequestError}
     * @throws {Error}
     */
    public async fetchBets(page: number): Promise<History[]> {
        const response = await NetworkUtils.processResponse(this.restService.fetchBets(page));
        return this.mappers.historyMapper.map(response.data as IHistoryDto[], true);
    }

    /**
     * @throws {AuthenticationError}
     * @throws {RequestError}
     * @throws {Error}
     */
    public fetchMessages() : Promise<Message[]> {
        return this.messageAction(this.restService.fetchMessages());
    }

    /**
     * @throws {AuthenticationError}
     * @throws {RequestError}
     * @throws {Error}
     */
    public async respondToMessage(actionId: number): Promise<Message[]> {
        const response = await this.restService.respondToMessage(actionId);
        if (['ActionAlreadyCompleted', 'PopupActionDoesNotExist'].includes(response.data.errorCode)) {
            throw new Error(response.data.errorCode);
        }

        return this.messageAction(response);
    }
    /**
     * @throws {AuthenticationError}
     * @throws {RequestError}
     * @throws {Error}
     */
    public async cancelWithdrawal(transaction: History): Promise<any> {
        const response = await NetworkUtils.processResponse(this.restService.cancelWithdrawal(transaction));

        if (response.data.StatusCode !== 'Success') {
            throw new RequestError(new Error(JSON.stringify(response)));
        }

        return response;
    }

    public async updatePhone(phone: string): Promise<any> {
        const response = await this.restService.updatePhone(phone);

        if (response.error) {
            if (response.errorCode === 'PlayerSessionCheckFailed') {
                throw new AuthenticationError();
            }

            if (response.fieldErrors && response.fieldErrors.phone) {
                throw new ValidationError({
                    phone: [response.fieldErrors.phone]
                });
            } else if (response.notice) {
                if (response.notice === smsLimitNotice) {
                    throw new SmsLimitReachedError();
                } else {
                    throw new ValidationError({
                        general: [response.notice]
                    });
                }
            } else {
                throw NetworkUtils.getRequestError(response);
            }
        }
    }

    public async uploadIdCard(file: string, fileName: string): Promise<any> {
        const response = await this.restService.uploadIdCard(file, fileName);

        if (response.error) {
            if (response.errorCode === 'PlayerSessionCheckFailed') {
                throw new AuthenticationError();
            }

            if (response.errorCode === 'DocumentUploadLimitExceeded') {
                throw new ValidationError({
                    file: ['Document limit exceeded. Please contact our support']
                });
            }

            if (response.errorCode === 'DocumentFormatNotSupported') {
                throw new ValidationError({
                    file: ['document not supported']
                });
            } else if (response.notice) {
                throw new ValidationError({
                    general: [response.notice]
                });
            } else {
                throw NetworkUtils.getRequestError(response);
            }
        }
    }

    public async sendSmsCode(): Promise<any> {
        const response = await this.restService.sendSmsCode();

        if (response.error) {
            if (response.errorCode === 'PlayerSessionCheckFailed') {
                throw new AuthenticationError();
            }

            if (response.errorCode === 'ContactInfoVerificationRequestFailed') {
                throw new ValidationError({
                    reason: ['sending sms failure']
                });
            } else if (response.notice) {
                throw new ValidationError({
                    reason: [response.notice]
                });
            } else {
                throw NetworkUtils.getRequestError(response);
            }
        }
    }

    public async validateSmsCode(code: string): Promise<any> {
        const response = await this.restService.validateSmsCode(code);

        if (response.error) {
            if (response.errorCode === 'PlayerSessionCheckFailed') {
                throw new AuthenticationError();
            }

            if (response.errorCode === 'VerifyPlayerCodeFailed') {
                throw new ValidationError({
                    code: ['wrong code']
                });
            } else if (response.notice) {
                throw new ValidationError({
                    general: [response.notice]
                });
            } else {
                throw NetworkUtils.getRequestError(response);
            }
        }
    }

    public updatePrivacy(acceptsGeneralPrivacy: boolean, acceptsCustomPrivacy: boolean): Promise<any> {
        return NetworkUtils.processResponse(
            this.restService.updatePrivacy(acceptsGeneralPrivacy, acceptsCustomPrivacy)
        );
    }

    public async getLottoFavorites(): Promise<Favourites> {
        const response: any = await NetworkUtils.processResponse(this.restService.getLottoFavorites());
        return lottoFavouriteMapperIn.createTargetObject(response.data.SettingValue);

    }

    public async setLottoFavorites(favourites: Record<number, number[][]>) {
        try {
            const body = lottoFavouriteMapperOut.createTargetObject(favourites);
            const response: any = await NetworkUtils.processResponse(this.restService.setLottoFavorites(body));
            return response.data.SettingValue;
        } catch (error) {
            const { notice } = JSON.parse(error.message);
            if (notice === '#LOTOFAVORITESMAXREACHED') {
                throw new MaxFavoritesReachedError();
            } else {
                throw error;
            }
        }
    }
    /**
     * @throws {AuthenticationError}
     * @throws {RequestError}
     * @throws {Error}
     */
    private async messageAction(responsePromise: Promise<any>) {
        const response = await NetworkUtils.processResponse(responsePromise) as IResponse<IMessageDataDto>;
        return messageMapper.map(response.data.Messages, true);
    }

    public static clearInstance() {
        if (process.env.NODE_ENV !== 'test') {
            throw new Error('For use in tests only');
        }
        delete UserService.instance;
    }
}

export default UserService as Singleton<UserService>;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--12!./src/core/services/user/UserService.ts